import React, { useEffect, useRef, useState } from 'react';
import { EuiLoadingSpinner } from '@elastic/eui';
import { Capacitor, registerPlugin } from '@capacitor/core';
import './GoogleSignInButton.css';

const GOOGLE_SCRIPT_ID = 'google-identity-services';
let googleScriptPromise;
const NativeGoogleAuth = registerPlugin('NativeGoogleAuth');

const loadGoogleIdentityServices = () => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (googleScriptPromise) return googleScriptPromise;
  googleScriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(GOOGLE_SCRIPT_ID);
    const script = existing || document.createElement('script');
    const onLoad = () => resolve();
    const onError = () => reject(new Error('無法載入 Google 登入服務，請檢查網路後再試。'));
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });
    if (!existing) {
      script.id = GOOGLE_SCRIPT_ID;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return googleScriptPromise;
};

const GoogleSignInButton = ({ onSuccess, onError }) => {
  const containerRef = useRef(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const [submitting, setSubmitting] = useState(false);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const isNativeApp = Capacitor.isNativePlatform();
  const nativeGoogleSignInEnabled = process.env.REACT_APP_NATIVE_GOOGLE_LOGIN_ENABLED === 'true';
  const hasNativeGoogleAuth = !isNativeApp || Capacitor.isPluginAvailable('NativeGoogleAuth');
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const finishGoogleSignIn = async (credential) => {
    if (!credential) throw new Error('Google 沒有返回登入憑證，請再試一次。');
    const response = await fetch('/api/users/google-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.message || 'Google 登入失敗，請稍後再試。');
    onSuccessRef.current(body);
  };

  const handleNativeGoogleSignIn = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await NativeGoogleAuth.signIn({ serverClientId: clientId });
      await finishGoogleSignIn(result?.credential);
    } catch (error) {
      const message = `${error?.message || error || ''}`;
      if (!/cancel|取消/i.test(message)) {
        onErrorRef.current(message || 'Google 登入失敗，請稍後再試。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!clientId || isNativeApp || !containerRef.current) return undefined;
    let active = true;
    loadGoogleIdentityServices()
      .then(() => {
        if (!active || !containerRef.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async ({ credential }) => {
            if (!credential) return;
            setSubmitting(true);
            try {
              await finishGoogleSignIn(credential);
            } catch (error) {
              onErrorRef.current(error.message || 'Google 登入失敗，請稍後再試。');
            } finally {
              if (active) setSubmitting(false);
            }
          },
        });
        containerRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text: 'continue_with',
          logo_alignment: 'left',
          width: Math.max(240, Math.min(360, containerRef.current.clientWidth || 320)),
        });
      })
      .catch((error) => onErrorRef.current(error.message));
    return () => { active = false; };
  }, [clientId, isNativeApp]);

  if (!clientId) return null;
  // Keep Google login hidden in installed apps until both native releases are ready.
  // Web browsers continue to use Google Identity Services normally.
  if (isNativeApp && !nativeGoogleSignInEnabled) return null;
  if (isNativeApp && !hasNativeGoogleAuth) {
    return <div className="google-auth-update-notice">
      請更新 App 後使用 Google 登入，或暫時使用郵箱登入。
    </div>;
  }
  return <div className="google-auth-wrap">
    {isNativeApp
      ? <button
          type="button"
          className="google-auth-native-button"
          disabled={submitting}
          onClick={handleNativeGoogleSignIn}
        >
          <span className="google-auth-native-logo" aria-hidden="true">G</span>
          <span>透過 Google 繼續操作</span>
        </button>
      : <div ref={containerRef} className="google-auth-button" />}
    {submitting && <div className="google-auth-loading"><EuiLoadingSpinner size="m" /> 正在登入…</div>}
  </div>;
};

export default GoogleSignInButton;
